import React, { useContext } from 'react';
import some from 'lodash/some';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import _get from 'lodash/get';

import BundleCouponPrice from 'Features/Bundle/components/Commerce/BundleCouponPrice';
import BundleKeySellingPoints from '../KeySellingPointBars/BundleKeySellingPoints';
import BundlePrice from 'Components/Price/BundlePrice';
import BundleRedesignBookPoints from 'Features/Bundle/components/BundleRedesign/BundleRedesignBookPoints';
import BundleRedesignProducts from 'Features/Bundle/components/BundleRedesign/BundleRedesignProducts';
import BundleRedesignTierBoxes from './BundleRedesignTierBoxes';
import BundleRedesignTieredProducts from 'Features/Bundle/components/BundleRedesign/BundleRedesignTieredProducts';
import CouponSaving from 'Components/Coupon/CouponSaving';
import FluidImg from 'Components/Utility/FluidImg';
import GiveawayRequired from 'Components/Giveaway/GiveawayRequired';
import GiveawaySoldOut from 'Components/Giveaway/GiveawaySoldOut';
import PaymentCards from 'Components/PaymentCards/PaymentCards';
import Price from 'Components/Utility/Price/Price';
import ProductAddToCartBtn from 'Components/ProductAddToCartBtn/ProductAddToCartBtn';
import ProductRequirements from 'Helpers/Product/ProductRequirements';
import ProductTimer from 'Components/Timers/ProductTimer';
import RegionLocked from 'Components/Regional/RegionLocked';
import StarDealCountdown from 'Components/StarDeal/StarDealCountdown';
import StarDealLabel from 'Components/StarDeal/StarDealLabel';
import WishlistButton from 'Components/Wishlist/WishlistButton';
import { countryCode } from 'Helpers/Geo/data';
import SelectedTierContext from 'Features/Bundle/context/selected-tier-context';

const BundleDesktopLayout = ({
  product = null,
  switchCarouselGame = () => { },
  bundleTiers = [],
  totalGameCount = 0,
  deliveredGameCount = 0,
  filesTotal = 0,
  downloadTotal = 0,
  pageTotal = 0,
  voucherDelivery = false,
  mixedBundle = false,
  validDrm = false,
  downloadTypes = [],
  preSelectedPnm = false,
  endDate,
  isStarDeal = false,
  displayTypes = [],
  couponIncluded = false,
}) => {
  const { selectedTier, inCartTier } = useContext(SelectedTierContext);

  const subscribed = useSelector(state => state.auth?.email_newsletter);
  const voucher = useSelector(state => state.voucher?.voucher);

  if (!product) return null;
  const isTiered = product?.bundles?.length > 1;

  const {
    cover,
    name,
    presale,
    slug,
    sold_out: soldOut,
    template_type: templateType,
    current_discount: currentDiscount,
    giveaway,
    giveaway_newsletter: giveawayNewsletter,
    discount_percent_override: discountPercentOverride,
    regions_included: includedRegions,
    regions_excluded: excludedRegions,
    bundles,
    currentPrice,
    fullPrice,
    hideDiscount,
    price,
  } = product;

  let LockedInRegion = false;
  if (excludedRegions.length >= 1 && some(excludedRegions, { code: countryCode })) {
    LockedInRegion = true;
  }
  if (includedRegions.length >= 1 && !some(includedRegions, { code: countryCode })) {
    LockedInRegion = true;
  }

  let validCoupon = ProductRequirements.validCoupon(product, voucher);
  const totalSaving = ProductRequirements.totalSaving(product, validCoupon);
  if (totalSaving.isMonetary) validCoupon = false;

  const keysAvailablePercentage = ProductRequirements.starDealKeyAvailablePercentage(product);
  const allEqual = arr => arr.every(v => v === arr[0]);

  return (
    <div className="BundleDesktopLayout container">
      <div className="BundlePageRedesign__content_left">
        {validDrm[0] === 'idw' &&
          <div className="product-message-banner">
            <div className="message-container">
              <FormattedMessage
                id="bundlepageredesign.message.idw.message"
                defaultMessage="To access this content, you need to download the free IDW app and create or log in to your account."
              />
              &nbsp;
              <a href="https://support.fanatical.com/hc/en/articles/23324336331538" target="_blank" rel="noopener noreferrer">
                <FormattedMessage
                  id="bundlepageredesign.message.idw.link"
                  defaultMessage="Find out more."
                />
              </a>
            </div>
          </div>
        }
        {isTiered ?
          <BundleRedesignTieredProducts
            switchCarouselGame={switchCarouselGame}
            bundleTiers={bundleTiers}
            bundleSlug={product.slug}
            templateType={templateType}
          />
          :
          <BundleRedesignProducts
            switchCarouselGame={switchCarouselGame}
            bundleTiers={bundleTiers}
            bundleSlug={product.slug}
            templateType={templateType}
          />
        }
      </div>
      <aside className="BundlePageRedesign__content__right">
        {isStarDeal &&
          <StarDealLabel flipLabel />
        }

        <div className="BundlePageRedesign__cover">
          <FluidImg
            className="img-force-full"
            src={cover}
            alt={name}
            lazy={false}
            sizesOverride="(min-width: 576px) 524px, calc(100vw - 1rem)"
          />
        </div>

        {isTiered &&
          <div className="BundlePageRedesign__saveMoreMessage">
            <FormattedMessage
              id="bundlepageredesign.blub"
              defaultMessage="Choose a bundle starting from just {savingText}. Bigger bundles include everything in the smaller ones!"
              values={{
                savingText: <span className="BundlePageRedesign__saveMoreMessage--primary">
                  <FormattedMessage
                    id="bundlepageredesign.savingText"
                    defaultMessage="{count} for {price}"
                    values={{
                      count: (bundles?.[0]?.games || []).filter(game => game.display)?.length,
                      price: <Price price={currentPrice || price} />,
                    }}
                  />
                </span>,
              }}
            />
          </div>
        }

        {product.template_type === 'book' && !isTiered &&
          <div className="BundlePageRedesign__saveMoreMessage">
            <FormattedMessage
              id="bundlepageredesign.book-value"
              defaultMessage="Get {deliveredGameCount} {displayType} worth {price}"
              values={{
                displayType: allEqual(displayTypes) ? 'eBooks' : 'products',
                price: <Price price={bundles[bundles.length - 1].incrementalFullPrice} />,
                deliveredGameCount: couponIncluded ? deliveredGameCount - 1 : deliveredGameCount,
              }}
            />
          </div>
        }

        <div className="BundlePageRedesign__stickyContainer">
          {!validCoupon && !isTiered &&
            <BundlePrice
              bundles={bundles}
              price={currentPrice || price}
              fullPrice={fullPrice}
              hideDiscount={hideDiscount}
              bundleTemplate={product.template_type}
              giveaway={giveaway}
              discountPercentOverride={discountPercentOverride}
              presale={presale}
              payWhatYouWant={false}
              validCoupon={validCoupon}
              totalGameCount={totalGameCount}
            />
          }

          {validCoupon &&
            <React.Fragment>
              <BundleCouponPrice
                price={currentPrice}
                fullPrice={fullPrice}
                couponDiscountPercent={(_get(validCoupon, 'percent', 0) / 100)}
                hideDiscount={hideDiscount}
                totalSavingPercent={totalSaving.percent}
                mystery={product.mystery}
              />

              <div className="mb-4">
                <CouponSaving
                  hasExtraSaving={totalSaving.hasExtraSaving}
                  savingPrice={totalSaving.price}
                  coupon={validCoupon}
                  extraSavingPrice={totalSaving.extraSavingPrice}
                  isMonetary={totalSaving.isMonetary}
                />
              </div>
            </React.Fragment>
          }

          {soldOut && !window.location.pathname.includes('/preview/') &&
            <div className="BundleDesktopLayout__soldOutMessage">
              <FormattedMessage id="bundledesktoplayout.bundle-now-ended" defaultMessage="Sorry - This bundle has now ended." />
            </div>
          }

          {giveaway && giveawayNewsletter && !subscribed && !soldOut &&
            <GiveawayRequired />
          }
          {giveaway && giveawayNewsletter && soldOut &&
            <GiveawaySoldOut subscribed={subscribed} />
          }

          {isTiered &&
            <BundleRedesignTierBoxes
              bundleTiers={bundleTiers}
            />
          }


          {!presale &&
            <div className={`BundleDesktopLayout__buttonWishlistContainer ${soldOut ? 'sold-out' : ''}`}>
              {!soldOut && !LockedInRegion &&
                <ProductAddToCartBtn
                  product={product}
                  preSelectedPnm={preSelectedPnm}
                  inCartTier={inCartTier}
                  selectedTier={selectedTier}
                />
              }

              {!preSelectedPnm &&
                <WishlistButton
                  bundleButton
                  slug={slug}
                  productId={product._id}
                  product={product}
                />
              }
            </div>
          }

          {product.template_type === 'book' && isTiered &&
            <div className="BundlePageRedesign__saveMoreMessage">
              <FormattedMessage
                id="bundlepageredesign.book-value"
                defaultMessage="Get {deliveredGameCount} {displayType} worth {price}"
                values={{
                  displayType: allEqual(displayTypes) ? 'eBooks' : 'products',
                  price: <Price price={bundles[bundles.length - 1].incrementalFullPrice} />,
                  deliveredGameCount: couponIncluded ? deliveredGameCount - 1 : deliveredGameCount,
                }}
              />
            </div>
          }
          {!soldOut &&
            <ProductTimer
              bundlePage
              startDate={0}
              endDate={endDate ? Date.parse(endDate) : 0}
              dayRestriciton={19}
            />
          }

          {isStarDeal && !soldOut &&
            <StarDealCountdown
              keysAvailablePercentage={keysAvailablePercentage}
              soldOut={soldOut}
              currentDiscount={currentDiscount}
            />
          }

          <RegionLocked shortText included={includedRegions} excluded={excludedRegions} />
          <PaymentCards />
          {slug !== 'stardock-software-bundle' &&
            <BundleKeySellingPoints
              product={product}
              totalGameCount={totalGameCount}
              totalComicCount={filesTotal || downloadTotal}
              totalPageCount={pageTotal}
              validDrm={validDrm}
              voucherDelivery={voucherDelivery}
              downloadTypes={downloadTypes}
              mixedBundle={mixedBundle}
              deliveredGameCount={deliveredGameCount}
            />
          }

          {templateType === 'book' &&
            <BundleRedesignBookPoints
              mixedBundle={mixedBundle}
              downloadTypes={downloadTypes}
              slug={slug}
              drm={validDrm}
            />
          }

        </div>
      </aside >
    </div >
  );
};

export default BundleDesktopLayout;
